import React from 'react';
import { Review } from '../../types/pms';
import { getSelfReviewProgress, SelfReviewProgress } from '../../services/pmsWorkflow';
import './ReviewProgressSummary.css';

interface ReviewProgressSummaryProps {
  reviews: Review[];
  totalEmployees: number;
  title?: string;
}

/**
 * Objectives / Self-Review / Manager-Review / Overall % summary widget.
 * Used by the manager's team view (and reusable for an HR-level aggregate view)
 * so a manager scanning many employees can see review-cycle progress at a glance.
 */
const ReviewProgressSummary: React.FC<ReviewProgressSummaryProps> = ({ reviews, totalEmployees, title }) => {
  const managerCompleted = reviews.filter(r => getSelfReviewProgress(r) === SelfReviewProgress.MANAGER_SUBMITTED).length;
  const selfSubmittedTotal = reviews.filter(r => getSelfReviewProgress(r) !== SelfReviewProgress.NOT_STARTED).length;
  const selfSubmittedOnly = selfSubmittedTotal - managerCompleted;
  const notStarted = Math.max(0, totalEmployees - selfSubmittedTotal);
  const overallPercent = totalEmployees > 0 ? Math.round((managerCompleted / totalEmployees) * 100) : 0;

  return (
    <div className="pms-review-progress-summary">
      {title && <h3 className="pms-review-progress-summary__title">{title}</h3>}
      <div className="pms-review-progress-summary__row">
        <div className="pms-review-progress-summary__stat">
          <span className="pms-review-progress-summary__value">{totalEmployees}</span>
          <span className="pms-review-progress-summary__label">Employees</span>
        </div>
        <div className="pms-review-progress-summary__stat">
          <span className="pms-review-progress-summary__value pms-review-progress-summary__value--waiting">{notStarted}</span>
          <span className="pms-review-progress-summary__label">Waiting for Employee</span>
        </div>
        <div className="pms-review-progress-summary__stat">
          <span className="pms-review-progress-summary__value pms-review-progress-summary__value--ready">{selfSubmittedOnly}</span>
          <span className="pms-review-progress-summary__label">Self Review Submitted</span>
        </div>
        <div className="pms-review-progress-summary__stat">
          <span className="pms-review-progress-summary__value pms-review-progress-summary__value--done">{managerCompleted}</span>
          <span className="pms-review-progress-summary__label">Manager Completed</span>
        </div>
      </div>
      <div className="pms-review-progress-summary__bar-wrap">
        <div className="pms-review-progress-summary__bar-track">
          <div className="pms-review-progress-summary__bar-fill" style={{ width: `${overallPercent}%` }} />
        </div>
        <span className="pms-review-progress-summary__bar-label">{overallPercent}% complete</span>
      </div>
    </div>
  );
};

export default ReviewProgressSummary;
